import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AnimateSection from '@/components/AnimateSection';
import { useMetaTags } from '@/hooks/use-meta-tags';
import { ProjectDetail } from '@/components/ProjectDetail';
import { useProjects } from '@/hooks/use-content';
import type { ProjectRow as Project } from '@/types/database';
import { generateProjectSlug } from '@/lib/slug';
import Navbar from '@/components/sections/Navbar';
import Hero from '@/components/sections/Hero';
import About from '@/components/sections/About';
import Skills from '@/components/sections/Skills';
import Projects from '@/components/sections/Projects';
import Websites from '@/components/sections/Websites';
import Designs from '@/components/sections/Designs';
import Videos from '@/components/sections/Videos';
import Extensions from '@/components/sections/Extensions';
import Certificates from '@/components/sections/Certificates';
import Services from '@/components/sections/Services';
import Contact from '@/components/sections/Contact';
import Footer from '@/components/sections/Footer';

const DEFAULT_META = {
  title: "Ahmad Abid - Software Engineer | Android, Kotlin & Mobile Development Specialist",
  description: "Software engineer specializing in native Android development with Kotlin and Jetpack Compose, cross-platform apps with Flutter, and full-stack web development. Also offering video editing with Canva. Hire on Fiverr or Upwork.",
  keywords: "Software engineer, Android developer, native Android development, Kotlin developer, Jetpack Compose, Flutter developer, Android developer for hire, mobile app development, full-stack web developer, web developer, Firebase, React, Android/iOS, app developer, UI/UX developer, video editor, Canva video editing, video editing services, freelancer, Fiverr Android developer, Upwork freelancer, Android Studio, Dart, Supabase",
  canonical: "https://ahmadabid.com",
};

// Main Component
const Index = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { data: projectsData } = useProjects();

  // The project modal's open/selected state is derived entirely from the
  // route (/projects/:slug) rather than local state, so a project's URL is a
  // real, shareable, bookmarkable link instead of a client-only hash.
  const selectedProject = useMemo(() => {
    if (!slug || !projectsData) return null;
    return projectsData.find((p) => generateProjectSlug(p.title) === slug) ?? null;
  }, [slug, projectsData]);

  const isProjectModalOpen = Boolean(slug && selectedProject);

  // A slug that doesn't match any project (stale/bad link) — once data has
  // loaded and there's still no match, bounce back to the homepage.
  useEffect(() => {
    if (slug && projectsData && !selectedProject) {
      navigate('/', { replace: true });
    }
  }, [slug, projectsData, selectedProject, navigate]);

  useMetaTags(
    selectedProject
      ? {
          title: `${selectedProject.title} — Ahmad Abid`,
          description: selectedProject.description,
          canonical: `https://ahmadabid.com/projects/${generateProjectSlug(selectedProject.title)}`,
          image: selectedProject.screens?.[0],
        }
      : DEFAULT_META,
  );

  // A crawler that reaches a project via client-side nav (rather than
  // loading /projects/:slug directly, which gets its own prerendered HTML
  // with this same block baked in — see scripts/prerender-projects.mjs)
  // still needs this structured data injected once the route changes.
  useEffect(() => {
    if (!selectedProject) return;
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: selectedProject.title,
      description: selectedProject.description,
      image: selectedProject.screens?.[0],
      url: `https://ahmadabid.com/projects/${generateProjectSlug(selectedProject.title)}`,
      applicationCategory: 'MobileApplication',
      operatingSystem: 'Android',
      author: { '@type': 'Person', name: 'Ahmad Abid', url: 'https://ahmadabid.com' },
    });
    document.head.appendChild(schema);
    return () => {
      if (schema.parentNode) schema.parentNode.removeChild(schema);
    };
  }, [selectedProject]);

  // Stable across renders (unlike an inline arrow function) so ProjectCard's
  // React.memo actually prevents re-rendering every card whenever this page
  // re-renders for an unrelated reason.
  const handleProjectClick = useCallback(
    (project: Project) => {
      navigate(`/projects/${generateProjectSlug(project.title)}`);
    },
    [navigate],
  );

  const handleCloseProjectModal = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Comprehensive JSON-LD Schema for SEO
  useEffect(() => {
    // Person Schema
    const personSchema = document.createElement('script');
    personSchema.type = 'application/ld+json';
    personSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Ahmad Abid",
      "jobTitle": "Software Engineer | Android & Kotlin Specialist | Mobile App Developer",
      "url": "https://ahmadabid.com",
      "email": "ahmadyousafzai30@gmail.com",
      "telephone": null,
      "image": "https://ahmadabid.com/profile.jpg",
      "description": "Software engineer with a primary focus on mobile development — expertise in Kotlin, Jetpack Compose, Flutter, and Firebase — plus full-stack web development experience building live production websites.",
      "sameAs": [
        "https://www.linkedin.com/in/ahmad-abid-1a57a3227/",
        "https://github.com/khanyousafzaideveloper",
        "https://www.fiverr.com/khanyousafza662",
        "https://www.upwork.com/freelancers/~01c53cabd074392a06"
      ],
      "givenName": "Ahmad",
      "familyName": "Abid",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Peshawar",
        "addressRegion": "KP",
        "addressCountry": "PK"
      },
      "worksFor": {
        "@type": "Organization",
        "name": "Softech Square Software Solutions Pvt Ltd"
      },
      "knowsAbout": [
        "Software Engineering",
        "Native Android Development",
        "Android Development",
        "Kotlin",
        "Jetpack Compose",
        "Flutter",
        "Firebase",
        "React",
        "Mobile App Development",
        "Full-Stack Web Development",
        "Web Development",
        "Browser Extension Development",
        "UI/UX Design",
        "App Optimization",
        "Video Editing",
        "Canva"
      ]
    });
    document.head.appendChild(personSchema);

    // Portfolio Schema
    const portfolioSchema = document.createElement('script');
    portfolioSchema.type = 'application/ld+json';
    portfolioSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Ahmad Abid - Software Engineer Portfolio",
      "url": "https://ahmadabid.com",
      "description": "Portfolio showcasing mobile app development (Android, Kotlin, Flutter) alongside full-stack web and browser extension projects",
      "creator": {
        "@type": "Person",
        "name": "Ahmad Abid"
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://ahmadabid.com"
        }
      }
    });
    document.head.appendChild(portfolioSchema);

    // Service Schema
    const serviceSchema = document.createElement('script');
    serviceSchema.type = 'application/ld+json';
    serviceSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Ahmad Abid - Software Engineer",
      "image": "https://ahmadabid.com/profile.jpg",
      "description": "Professional mobile app development (Android, Kotlin, Flutter) and full-stack web development services",
      "url": "https://ahmadabid.com",
      "email": "ahmadyousafzai30@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Peshawar",
        "addressRegion": "KP",
        "addressCountry": "PK"
      },
      "priceRange": "$$",
      "areaServed": "Worldwide",
      "serviceType": [
        "Native Android App Development",
        "Flutter App Development",
        "Mobile App Development",
        "Full-Stack Web Development",
        "Browser Extension Development",
        "App Optimization",
        "UI/UX Design",
        "Video Editing"
      ]
    });
    document.head.appendChild(serviceSchema);

    // BreadcrumbList Schema
    const breadcrumbSchema = document.createElement('script');
    breadcrumbSchema.type = 'application/ld+json';
    breadcrumbSchema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://ahmadabid.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "About",
          "item": "https://ahmadabid.com#about"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Skills",
          "item": "https://ahmadabid.com#skills"
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": "Projects",
          "item": "https://ahmadabid.com#projects"
        },
        {
          "@type": "ListItem",
          "position": 5,
          "name": "Websites",
          "item": "https://ahmadabid.com#websites"
        },
        {
          "@type": "ListItem",
          "position": 6,
          "name": "Designs",
          "item": "https://ahmadabid.com#designs"
        },
        {
          "@type": "ListItem",
          "position": 7,
          "name": "Video Editing",
          "item": "https://ahmadabid.com#videos"
        },
        {
          "@type": "ListItem",
          "position": 8,
          "name": "Extensions",
          "item": "https://ahmadabid.com#extensions"
        },
        {
          "@type": "ListItem",
          "position": 9,
          "name": "Certificates",
          "item": "https://ahmadabid.com#certificate"
        },
        {
          "@type": "ListItem",
          "position": 10,
          "name": "Services",
          "item": "https://ahmadabid.com#services"
        }
      ]
    });
    document.head.appendChild(breadcrumbSchema);

    return () => {
      if (personSchema.parentNode) personSchema.parentNode.removeChild(personSchema);
      if (portfolioSchema.parentNode) portfolioSchema.parentNode.removeChild(portfolioSchema);
      if (serviceSchema.parentNode) serviceSchema.parentNode.removeChild(serviceSchema);
      if (breadcrumbSchema.parentNode) breadcrumbSchema.parentNode.removeChild(breadcrumbSchema);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <AnimateSection><Hero /></AnimateSection>
      <AnimateSection><About /></AnimateSection>
      <AnimateSection><Skills /></AnimateSection>
      <AnimateSection><Projects onProjectClick={handleProjectClick} /></AnimateSection>
      <AnimateSection><Websites /></AnimateSection>
      <AnimateSection><Designs /></AnimateSection>
      <AnimateSection><Videos /></AnimateSection>
      <AnimateSection><Extensions /></AnimateSection>
      <AnimateSection><Certificates /></AnimateSection>
      <AnimateSection><Services /></AnimateSection>
      <AnimateSection><Contact /></AnimateSection>
      <Footer />

      {/* Project Detail Modal */}
      <ProjectDetail
        project={selectedProject}
        isOpen={isProjectModalOpen}
        onClose={handleCloseProjectModal}
      />
    </div>
  );
};

export default Index;
